/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard (C) Copyright IBM Corp. 2020, 2022
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'../../nls/StringResources',
	'react',
	'prop-types',
	'./CachingPageContainer',
	'./TransientStateManager',
	'./pages/DataSourcePanelEmpty',
	'./pages/DataSourcePanelList',
	'./pages/DataSourcePanelMetadataTree'

], function (
	_,
	StringResources,
	React,
	PropTypes,
	CachingPageContainer,
	TransientStateManager,
	DataSourcePanelEmpty,
	DataSourcePanelList,
	DataSourcePanelMetadataTree
) {

	const TRANSIENT_STATE_KEY = 'dataSourcePaneState';
	const PAGE_ID__EMPTY = 'noDataSources';
	const PAGE_ID__SOURCE_LIST = 'dataSourceList';
	const PAGE_ID__METADATA = 'metadata';

	const DATASOURCE_SELECTED_EVT = 'dataSourcePanel:dataSourceSelected';

	class DataSourcePanelComponent extends React.Component {

		static PAGE_TYPES = {
			EMPTY: PAGE_ID__EMPTY,
			SOURCE_LIST: PAGE_ID__SOURCE_LIST,
			METADATA: PAGE_ID__METADATA
		}

		static propTypes = {
			dashboardApi: PropTypes.object.isRequired,
			transientState: PropTypes.shape({
				get: PropTypes.func.isRequired,
				set: PropTypes.func.isRequired
			}).isRequired,
			actionHandlers: PropTypes.shape({
				addDataSource: PropTypes.func.isRequired
			}).isRequired
		};

		constructor(...args) {
			super(...args);

			this._setupEvents();

			//State not tracked by React
			this.transientStateManager = new TransientStateManager({
				transientState: this.props.transientState,
				transientStateKey: TRANSIENT_STATE_KEY,
				typeToCreator: {
					[PAGE_ID__EMPTY]: () => this._createEmptyPage(),
					[PAGE_ID__SOURCE_LIST]: () => this._createSourceListPage(),
					[PAGE_ID__METADATA]: ({ dataSourceId }) => this._createMetadataPage(dataSourceId)
				},
				validatePage: (type, payload) => this._validatePage(type, payload),
				onStateChange: () => this.forceUpdate()	//Connect non-React state to React render
			});

			this.state = {
				dataSources: this._getDataSources()
			};
			const currentPage = this.transientStateManager.getCurrentValue();
			if (!currentPage || !this._currentPageIsValid()) {
				this._setupInitialPage();
			}
		}


		_setupEvents() {
			this.registeredEvents = [];

			const dashboardApi = this.props.dashboardApi;
			this.registeredEvents.push(dashboardApi.on('widget:selected', event => this.onWidgetSelected(event)));
			this.registeredEvents.push(dashboardApi.on('dataSourceGrid:dataSourceSelected', event => this.onDataSourceSelected(event)));
			this.registeredEvents.push(dashboardApi.on('dataSourcePanel:dataSourceAdded', event => this.onUserDataSourceAdded(event)));

			const dataSourceCollection = this._getDataSourcesCollection();
			this.registeredEvents.push(dataSourceCollection.on('change:assetId', () => this.onDataSourceCollectionUpdated()));
			this.registeredEvents.push(dataSourceCollection.on('change:state', () => this.onDataSourceCollectionUpdated()));
			this.registeredEvents.push(dataSourceCollection.on('add', () => this.onDataSourceAdded()));
			this.registeredEvents.push(dataSourceCollection.on('remove', () => this.onDataSourceRemoved()));
			this.registeredEvents.push(dataSourceCollection.on('change:version', (event) => this.onDataSourceUpdated(event.model.id)));
		}

		_setupInitialPage() {
			if (this._getDataSources().length) {
				const activeDataSource = this._getActiveDataSource();
				if (activeDataSource) {
					this.showMetadataPage(activeDataSource, { resetFocus: true, triggerEvent: false });
				} else {
					this.showSourceListPage();
				}
			} else {
				this.showEmptyPage();
			}
		}

		_validatePage(type, payload) {
			if (type === PAGE_ID__EMPTY) {
				//Empty is only valid with no data sources
				return this._getDataSources().length === 0;
			}

			if (type === PAGE_ID__SOURCE_LIST) {
				//List is only valid with one or more data sources 
				return this._getDataSources().length > 0;
			}
			if (type === PAGE_ID__METADATA) {
				//Metadata tree is only valid if data source exists 
				//and the active source hasnt been updated while destroyed
				const dataSourceId = payload && payload.dataSourceId;
				const activeDataSource = this._getActiveDataSource();
				const isActiveDatasource = activeDataSource && activeDataSource.getId() === dataSourceId;
				const dataSource = dataSourceId && this._getDataSource(dataSourceId);
				return isActiveDatasource && !!(dataSource);
			}
			//Current page is not a valid value
			return false;

		}

		_currentPageIsValid() {
			const { type, payload } = this.transientStateManager.getCurrentDescriptor();
			return this._validatePage(type, payload);
		}

		_getDataSourcesCollection() {
			const dataSourcesFeature = this.props.dashboardApi.getFeature('dataSources.deprecated');
			return dataSourcesFeature && dataSourcesFeature.getSourcesCollection();
		}

		_getDataSources() {
			const sourcesCollection = this._getDataSourcesCollection();
			return sourcesCollection && sourcesCollection.getSources();
		}

		_getActiveDataSource() {
			const sourceList = this._getDataSources();
			if (sourceList && sourceList.length) {
				if (sourceList.length === 1) {
					const source = sourceList[0];
					if (source.getState() !== 'error') {
						return source;
					}
				} else {
					const activeSourceId = this.props.dashboardApi.getActiveDataSourceId();
					return activeSourceId && this._getDataSource(activeSourceId);
				}
			}
		}

		_getDataSource(id) {
			return this._getDataSources().find(dataSource => dataSource.getId() === id);
		}

		componentDidUpdate() {
			this._updateFocus();
		}

		componentDidMount() {
			this._updateFocus();
		}

		_updateFocus() {
			if (this.state.resetFocus) {
				const currentPage = this.transientStateManager.getCurrentValue();
				if (currentPage.ref && currentPage.ref.current) {
					const focusWasReset = currentPage.ref.current.resetFocus();
					if (focusWasReset) {
						this.setState({
							resetFocus: false
						});
					}
				}
			}
		}

		componentWillUnmount() {
			this.registeredEvents.forEach(
				({ remove }) => remove()
			);
			this.registeredEvents = [];
			this.transientStateManager.destroy();
		}

		render() {
			const pageList = this.transientStateManager.getValues();
			const currentPage = this.transientStateManager.getCurrentValue();

			return (
				<CachingPageContainer
					className='datasetPanelPageContainer'
					pages={pageList}
					currentPageId={currentPage && currentPage.id}
				/>
			);
		}

		// --- Cacheable page creators ---

		_createEmptyPage() {
			return {
				id: PAGE_ID__EMPTY,
				component: DataSourcePanelEmpty,
				className: 'datasetPanelPage',
				props: () => ({
					dashboardApi: this.props.dashboardApi,
					iconsFeature: this.props.dashboardApi.getFeature('Icons')
				})
			};
		}

		_createSourceListPage() {
			return {
				id: PAGE_ID__SOURCE_LIST,
				component: DataSourcePanelList,
				className: 'datasetPanelPage',
				props: () => ({
					dashboardApi: this.props.dashboardApi,
					dataSources: this.state.dataSources,
					moserDatasources: this.props.dashboardApi.getFeature('DataSources.moser'),
					onDataSourceSelected: (dataSource) => this.onDataSourceSelected(dataSource, true),
					iconsFeature: this.props.dashboardApi.getFeature('Icons')
				})
			};
		}

		_createMetadataPage(dataSourceId) {
			const ref = React.createRef();
			return {
				id: dataSourceId,
				component: DataSourcePanelMetadataTree,
				className: 'datasetPanelPage',
				ref,
				props: () => ({
					previousPages: [{
						id: PAGE_ID__SOURCE_LIST,
						title: StringResources.get('sourcePaneLabel'),
						onSelect: () => this.onShowDataSourceList()
					}],
					dashboardApi: this.props.dashboardApi,
					dataSource: this._getDataSource(dataSourceId),
					moserDatasources: this.props.dashboardApi.getFeature('DataSources.moser'),
					iconsFeature: this.props.dashboardApi.getFeature('Icons'),
					ref
				})
			};
		}

		// --- Internal state management ---

		showEmptyPage() {
			this.transientStateManager.setCurrentDescriptor(PAGE_ID__EMPTY);
		}

		showSourceListPage() {
			this.transientStateManager.setCurrentDescriptor(PAGE_ID__SOURCE_LIST);
		}

		showMetadataPage(
			dataSource,
			{ resetFocus = false, triggerEvent = true } = {}
		) {
			this.setState({
				resetFocus
			});
			this.transientStateManager.setCurrentDescriptor(
				PAGE_ID__METADATA,
				{ dataSourceId: dataSource.getId() }
			);
			this.props.dashboardApi.getFeature('DataSources').setActiveDataSourceId(dataSource.getId());
			if (triggerEvent) {
				this.props.dashboardApi.triggerDashboardEvent(DATASOURCE_SELECTED_EVT, {
					sender: dataSource.getId()
				});
			}
		}

		getCurrentPage() {
			const descriptor = this.transientStateManager.getCurrentDescriptor();
			return descriptor && descriptor.type;
		}

		removeMetadataPage(dataSourceId) {
			this.transientStateManager.removeDescriptor(
				PAGE_ID__METADATA,
				{ dataSourceId }
			);
		}

		// --- External events ---

		onWidgetSelected(event) {
			if (event.sender) {
				const dataSourcesCollection = this._getDataSourcesCollection();
				const dataSourceId = dataSourcesCollection && dataSourcesCollection.usesSource(event.sender);
				const dataSource = dataSourceId && dataSourcesCollection.getSource(dataSourceId);
				if (dataSource && dataSource.getState() !== 'error') {
					this.showMetadataPage(dataSource, { triggerEvent: false });
				}
			}
		}

		onDataSourceSelected(dataSource, triggerEvent = false) {
			this.showMetadataPage(dataSource, { resetFocus: true, triggerEvent: triggerEvent });
		}

		onUserDataSourceAdded(event) {
			const dataSource = this._getDataSource(event.sender);
			if (event.sender && dataSource) {
				this.showMetadataPage(dataSource);
			} else {
				this.showSourceListPage();
			}
		}

		_onDataSourceCollectionChange() {
			const dataSources = this._getDataSources();
			this.setState({
				dataSources
			});
			return dataSources;
		}

		onDataSourceCollectionUpdated() {
			this._onDataSourceCollectionChange();
		}

		onDataSourceAdded() {
			const dataSources = this._onDataSourceCollectionChange();

			if (this.getCurrentPage() === PAGE_ID__EMPTY) {
				// If a data source has been added, then it's not valid to show the empty page.
				// Usually this will be taken care of by onUserDataSourceAdded, but if the user
				// added a data source indirectly (e.g. redo) then we may need to change page:
				this.showMetadataPage(dataSources[0]);
			}
		}

		onDataSourceRemoved() {
			const dataSources = this._onDataSourceCollectionChange();

			// If the user explicitly removes a data source from DataSourcePanelList,
			// this is already a valid UI to display. If the user removes it by
			// another action in the UI (e.g. undo), then we may need to change page:
			if (dataSources && dataSources.length) {
				this.showSourceListPage();
			} else {
				this.showEmptyPage();
			}
		}

		onDataSourceUpdated(dataSourceId) {
			this.props.dashboardApi.triggerDashboardEvent(DATASOURCE_SELECTED_EVT, {
				sender: dataSourceId,
				refreshDatasetAction: true
			});

			this.removeMetadataPage(dataSourceId);

			const dataSource = this._getDataSource(dataSourceId);
			this.showMetadataPage(dataSource, { resetFocus: true });
		}

		onShowDataSourceList() {
			this.showSourceListPage();
		}

	}

	return DataSourcePanelComponent;
});
